import basketAPI from "../api/basketAPI";
import Basket from "../models/Basket";
import Customer from "../models/Customer";
import Attendees from "../models/Attendees";
import PaymentInfo from "../models/paymentInfo";

const state = {
  basket: {...new Basket()},
  customer: {...new Customer()},
  attendees: {...new Attendees()},
  agenda: {
    groups: [],
    concurrent_session: false
  },
  paymentInfo: {...new PaymentInfo()},
  isFetching: true,
  showIndicator: false,
  errorMessage: null,
  slug: '',
  token: '',
  step: '1',
  states: [],
  selectedSessionAddons: 0,
  selectedItemAddons: 0,
};

const actions = {
  fetchBasket({getters, commit}, {slug, token}) {
    commit('updateIsFetching', true);
    commit('updateSlug', slug);
    commit('updateToken', token);

    const params = {};
     if (getters.basketToken) {
       params.basket_token = getters.basketToken
     }
    return basketAPI.fetch(slug, params)
      .then(res => {
        commit('updateBasket', res.data.data);
      })
      .catch(err => {
        if (err?.message) {
          commit('updateErrorMessage', err?.message);
        }
      })
      .finally(() => {
        commit('updateIsFetching', false);
        commit('updateErrorMessage', null);
      });
  },

  updateBasket({state, commit, getters}, {tickets}) {
    commit('updateIndicator', true);

    const data = {
      tickets,
      _token: state.token,
      basket_token: getters.basketToken
    };
    if (getters.settings.donation_enabled && getters.donation) {
      data.donation = getters.donation;
    }

    return basketAPI.update(state.slug, data)
      .then(res => {
        commit('updateBasket', res.data.data);
        commit('updateErrorMessage', null);

        return res.data.data;
      })
      .catch(err => {
        if (err.message) {
          commit('updateErrorMessage', err.message);
        }
        return err;
      })
      .finally(() => {
        commit('updateIndicator', false);
      });
  },

  clearBasket({commit, getters}, {slug}) {
    commit('updateIndicator', true);

    const params = {
      basket_token: getters.basketToken
    };

    return basketAPI.clear(slug, params)
      .then(res => {
        commit('updateBasket', res.data.data);
      })
      .catch(err => {
        if (err?.message) {
          commit('updateErrorMessage', err?.message);
        }
      })
      .finally(() => {
        commit('updateIndicator', false);
      });
  },

  sendCoupon({state, getters, commit}, {slug, code, success, error}) {
    commit('updateIndicator', true);

    return basketAPI.sendCoupon(slug, {
      code,
      _token: state.token,
      basket_token: getters.basketToken
    })
      .then(res => {
        commit('updateBasket', res.data.data);
        commit('updateErrorMessage', null);
        success(res?.data?.data);
      })
      .catch(err => {
        if (err?.response?.data?.message) {
          commit('updateErrorMessage', err?.response?.data?.message);
        }
        error(err?.response?.data);
      })
      .finally(() => {
        commit('updateIndicator', false);
      });
  },

  fetchCustomer({state, getters, commit}) {
    commit('updateIsFetching', true);

    const params = {
      basket_token: getters.basketToken
    };

    return basketAPI.fetchCustomer(state.slug, params)
      .then(res => {
        commit('updateCustomer', res?.data?.data?.customer);
        commit('updateStates', res?.data?.data?.states);
      })
      .catch(err => {
        if (err?.message) {
          commit('updateErrorMessage', err?.message);
        }
      })
      .finally(() => {
        commit('updateIsFetching', false);
      });
  },

  updateCustomer({state, getters, commit}, {customer, success, error}) {
    commit('updateIndicator', true);

    return basketAPI.updateCustomer(state.slug, {
      ...customer,
      _token: state.token,
      basket_token: getters.basketToken
    })
      .then(res => {
        commit('updateCustomer', res?.data?.data);
        success(res?.data?.data);
      })
      .catch(err => {
        error(err?.response?.data);
        if (err?.response?.data?.message) {
          commit('updateErrorMessage', err?.response?.data?.message);
        }
      })
      .finally(() => {
        commit('updateIndicator', false);
      });
  },

  fetchAttendees({state, getters, commit}) {
    commit('updateIndicator', true);
    commit('updateIsFetching', true);

    const params = {
      basket_token: getters.basketToken
    };

    return basketAPI.fetchAttendees(state.slug, params)
      .then(res => {
        commit('updateAttendees', res?.data?.data);
      })
      .catch(err => {})
      .finally(() => {
        commit('updateIsFetching', false);
        commit('updateIndicator', false);
      });
  },

  updateAttendees({state, getters, commit, dispatch}, {attendees, success, error}) {
    commit('updateIndicator', true);

    const params = {
      attendees,
      _token: state.token,
      basket_token: getters.basketToken
    };

    return basketAPI.updateAttendees(state.slug, params)
      .then(res => {
        commit('updateAttendees', res?.data?.data);

        if (res?.data?.data?.wild_apricot_discount) {
          dispatch('fetchBasket', {slug: state.slug, token: state.token })
        }

        success(res?.data?.data);
      })
      .catch(err => {
        error(err?.response?.data);
        if (err?.response?.data?.message) {
          commit('updateErrorMessage', err?.response?.data?.message);
        }
      })
      .finally(() => {
        if (!getters.hasAgenda) {
          commit('updateIndicator', false);
        }
      });
  },

  updateWildApricotAttendeeDiscount({state, getters, commit}, {data, success, error}) {
    const params = {
      ...data,
      _token: state.token,
      basket_token: getters.basketToken
    };

    commit('updateIndicator', true);

    return basketAPI.updateWildApricotAttendeeDiscount(state.slug, params)
      .then(res => {
        commit('updateBasket', res.data.data);

        success(res?.data?.data);
      })
      .catch(err => {
        error(err?.response?.data);
        if (err?.response?.data?.message) {
          commit('updateErrorMessage', err?.response?.data?.message);
        }
      })
      .finally(() => {
        commit('updateIndicator', false);
      });
  },

  fetchAgendaGroups({state, getters, commit}) {
    commit('updateIsFetching', true);
    commit('updateIndicator', true);

    const params = {
      basket_token: getters.basketToken
    };

    return basketAPI.fetchAgendaGroups(state.slug, params)
      .then(res => {
        commit('updateAgendaGroups', res?.data?.data);
      })
      .catch(err => {})
      .finally(() => {
        commit('updateIsFetching', false);
        commit('updateIndicator', false);
      });
  },

  updateAgendaGroups({state, getters, commit}, {sessions, success, error}) {
    commit('updateIndicator', true);

    const params = {
      sessions,
      _token: state.token,
      basket_token: getters.basketToken
    };

    return basketAPI.updateAgendaGroups(state.slug, params)
      .then(res => {
        // commit('updateAgendaGroups', res?.data?.data);

        success(res?.data?.data);
      })
      .catch(err => {
        error(err?.response?.data);
        if (err?.response?.data?.message) {
          commit('updateErrorMessage', err?.response?.data?.message);
        }
      })
      .finally(() => {
        commit('updateIndicator', false);
      });
  },

  fetchPaymentInfo({state, getters, commit}, affirm) {
    commit('updateIsFetching', true);

    const data = {
      basket_token: getters.basketToken
    };
    if (affirm) {
      data.affirm = true;
    }

    return basketAPI.fetchPaymentInfo(state.slug, data)
      .then(res => {
        commit('updatePaymentInfo', res?.data?.data);
      })
      .catch(err => {})
      .finally(() => {
        commit('updateIsFetching', false);
      });
  },

  confirmSuccessPayment({state, getters, commit, dispatch}) {
    commit('updateIndicator', true);

    const params = {
      _token: state.token,
      basket_token: getters.basketToken
    };

    return basketAPI.confirmSuccessPayment(state.slug, params)
      .then(res => {
        if (res.data?.data?.payment_id) {
          dispatch('onHandleGoogleAnalytics', res.data?.data);
          dispatch('onHandleGoogleAds', res.data?.data);
        }
        dispatch('onHandleToSuccessPage');

        // success(res?.data?.data);
      })
      .catch(err => {
        // error(err?.response?.data);
        if (err?.response?.data?.message) {
          commit('updateErrorMessage', err?.response?.data?.message);
        }
      })
      .finally(() => {
        commit('updateIndicator', false);
      });
  },

  async changeStep({state, commit, dispatch, getters}, payload) {
    commit('updateErrorMessage', null);
    if (payload.type === "set") {
      commit('updateStep', payload.step);
    }

    if (payload.type === "back") {
      switch (state.step) {
        case "1.1":
          commit('updateStep', "1");
          break;
        case "2":
          const selectedTickets = getters.selectedTickets;

          if (selectedTickets === 0) {
            commit('updateErrorMessage', 'Please select ticket before doing something.');
          } else if (selectedTickets === 1) {
            commit('updateStep', "1");
          } else if (selectedTickets > 1) {
            await dispatch('fetchCustomer');
            commit('updateStep', "1.1");
          }
          break;
        case "2.1":
          await dispatch('fetchAttendees');
          commit('updateStep', "2");
          break;
        case "3":
          if (getters.hasAgenda) {
            await dispatch('fetchAgendaGroups');
            commit('updateStep', "2.1");
          } else {
            commit('updateStep', "2");
          }
          break;
        case "4":
          if (state.basket.order.summary.total_price) {
            await dispatch('fetchPaymentInfo');
            commit('updateStep', "3");
          } else if (getters.hasAgenda) {
            await dispatch('fetchAgendaGroups');
            commit('updateStep', "2.1");
          } else {
            commit('updateStep', "2");
          }
          break;
      }
    } else if (payload.type === "forward") {
      switch (state.step) {
        case "1":
          const selectedTickets = getters.selectedTickets;

          if (selectedTickets === 0) {
            commit('updateErrorMessage', 'Please select ticket before go to the next step.');
          } else if (selectedTickets === 1) {
            await dispatch('fetchAttendees');
            commit('updateStep', "2");
          } else if (selectedTickets > 1) {
            await dispatch('fetchCustomer');
            commit('updateStep', "1.1");
          }
          break;
        case "1.1":
          await dispatch('fetchAttendees');
          commit('updateStep', "2");
          break;
        case "2":
          if (getters.hasAgenda) {
            await dispatch('fetchAgendaGroups');

            if (!state.agenda?.concurrent_session && !state.agenda?.groups?.length) {
              if (state.basket.order.summary.total_price) {
                await dispatch('fetchPaymentInfo');
                commit('updateStep', "3");
              } else {
                await dispatch('fetchPaymentInfo');
                await dispatch('confirmSuccessPayment');
              }
            } else {
              commit('updateStep', "2.1");
            }
          } else if (state.basket.order.summary.total_price) {
            await dispatch('fetchPaymentInfo');
            commit('updateStep', "3");
          } else {
            await dispatch('fetchPaymentInfo');
            await dispatch('confirmSuccessPayment');
            //commit('updateStep', "4");
          }
          break;
        case "2.1":
          if (state.basket.order.summary.total_price) {
            await dispatch('fetchPaymentInfo');
            commit('updateStep', "3");
          } else {
            await dispatch('fetchPaymentInfo');
            await dispatch('confirmSuccessPayment');
            // commit('updateStep', "4");
          }
          break;
        case "3":
          await dispatch('confirmSuccessPayment');
          // commit('updateStep', "4");
          break;
      }
    }
  },

  onHandleGoogleAnalytics({state, getters}, payload) {
    if (payload?.payment_id && (getters.settings.google_analytics_id || getters.settings.google_tag_manager_id)) {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'eec.purchase',
        ecommerce: {
          currencyCode: state.basket.options.currency,
          purchase: {
            actionField: {
              id: payload?.payment_id,
              revenue: state.basket.order.summary.total_price
            }
          }
        }
      });
    }
  },

  onHandleGoogleAds({state, getters}, payload) {
    if (payload?.payment_id && getters.settings.google_ads_id && getters.settings.google_ads_conversation_id) {
      const amountInCents = state.basket.order.summary.total_price * 100;
      gtag_report_conversion(amountInCents, payload?.payment_id);
    }
  },

  onHandleToSuccessPage({state, getters, commit}, payload) {
    if (getters.settings.customSuccessEnabled && getters.settings.customSuccessUrl) {
      const url = `${getters.settings.customSuccessUrl}?event=${state.slug}&total=${state.basket.order.summary.total_price}`;
      window.location.replace(url);
    } else {
      commit('updateStep', '4');
    }
  }
};

const mutations = {
  updateBasket(state, payload) {
    state.basket = {
      ...state.basket,
      ...payload
    };
  },

  updateDonation(state, payload) {
    state.basket.order.summary.donation = payload;
  },

  updateCustomer(state, payload) {
    state.customer = {
      ...state.customer,
      ...payload
    };

    if (state.customer.email) {
      state.customer.email_confirmation = state.customer.email;
    }
  },

  updateStates(state, payload) {
    state.states = {
      ...state.states,
      ...payload
    };
  },

  updateAttendees(state, payload) {
    if (payload.attendees?.length > 1) {
      payload.attendees.forEach(attendee => {
        if (attendee.email) {
          attendee.email_confirmation = attendee.email;
        }
      })
    }
    state.attendees = {
      attendees: payload?.attendees,
      form: payload?.form,
      validation: payload?.validation,
      addons: {
        total_session_addons: payload?.addons?.total_session_addons,
        total_item_addons: payload?.addons?.total_item_addons
      }
    };
  },

  updateAgendaGroups(state, payload) {
    state.agenda = {
      groups: payload.agenda,
      concurrent_session: payload.concurrent_session
    }
  },

  updateStep(state, step) {
    state.step = step;
  },

  updateIndicator(state, indicator) {
    state.showIndicator = indicator;
  },

  updateIsFetching(state, isFetching) {
    state.isFetching = isFetching;
  },

  updateSlug(state, slug) {
    state.slug = slug;
  },

  updateToken(state, token) {
    state.token = token;
  },

  updateErrorMessage(state, msg) {
    state.errorMessage = msg;
  },

  updateSelectedSessionAddons(state, payload) {
    state.selectedSessionAddons = payload;
  },

  updateSelectedItemAddons(state, payload) {
    state.selectedItemAddons = payload;
  },

  updatePaymentInfo(state, payload) {
    state.paymentInfo = payload;
  },
};

const getters = {
  isFetching(state) {
    return state.isFetching;
  },

  showIndicator(state) {
    return state.showIndicator;
  },

  slug(state) {
    return state.slug;
  },

  errorMessage(state) {
    return state.errorMessage;
  },

  basket(state) {
    return state.basket;
  },

  basketToken(state) {
    return state.basket.basket_token;
  },

  conference(state) {
    return state.basket.conference;
  },

  settings(state) {
    return state.basket.settings;
  },

  wildApricotEnabled(state) {
    return state.basket.settings.wild_apricot_enabled;
  },

  donation(state) {
    return state.basket.order.summary.donation;
  },

  agenda(state) {
    return state.agenda;
  },

  hasAgenda(state) {
    return state.basket.conference.is_agenda_group;
  },

  order(state) {
    return state.basket.order;
  },

  tickets(state) {
    return state.basket.tickets;
  },

  selectedTickets(state) {
    let selectedTickets = 0;

    state.basket.tickets.inperson.forEach(el => {
      if (el.selected) {
        selectedTickets += +el.selected;
      }
    });
    state.basket.tickets.virtual.forEach(el => {
      if (el.selected) {
        selectedTickets += +el.selected;
      }
    });

    return selectedTickets;
  },

  currencySymbol(state) {
    return state.basket.options.currency_symbol;
  },

  step(state) {
    return state.step;
  },

  customer(state) {
    return state.customer;
  },

  states(state) {
    return state.states;
  },

  attendees(state) {
    return state.attendees.attendees;
  },

  attendeeForm(state) {
    const asteric = /\*<\/label>/gi;
    const newAsteric = '<span class="asteric">*</span><\/label>';
    return state.attendees.form.replace(asteric, newAsteric);
  },

  attendeeValidation(state) {
    return state.attendees.validation;
  },

  totalSessionAddons(state) {
    return state.attendees.addons.total_session_addons;
  },

  totalItemAddons(state) {
    return state.attendees.addons.total_item_addons;
  },

  showAddonsSummary(state) {
    return (+state.attendees.addons.total_session_addons + +state.attendees.addons.total_item_addons);
  },

  selectedSessionAddons(state) {
    return state.selectedSessionAddons;
  },

  selectedItemAddons(state) {
    return state.selectedItemAddons;
  },

  paymentInfo(state) {
    return state.paymentInfo;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
